/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/no-danger */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';

import { MarkdownBlock } from '../../../services/types/blob-types';

type Props = {
  content: string | null;
  blocks?: MarkdownBlock[];
};

/**
 * OCRPreview Component
 *
 * Renders a specialized XML-like document structure that contains markdown content in its leaf nodes,
 * with the exception of Table nodes, which contain raw HTML.
 *
 * This component handles a document format where:
 * - The overall structure is an XML-like tree (Document > Page > Sections > Text, etc.)
 * - Leaf nodes (Text, SectionHeader, etc.) contain markdown content
 * - Table nodes are special cases that contain HTML to be rendered directly
 *
 * The parser converts this hybrid format into React components, rendering:
 * - Tables as raw HTML (using dangerouslySetInnerHTML)
 * - Leaf nodes as markdown (using ReactMarkdown)
 * - Container nodes as divs with their children
 *
 * Hovering over any node reveals its tag name and citation ID for debugging.
 *
 * @example Input format:
 * ```
 * <Document citation-id=document>
 *   <Page citation-id=/page/0/Page/1>
 *     <SectionHeader citation-id=/page/0/SectionHeader/2>**Title**</SectionHeader>
 *     <Text citation-id=/page/0/Text/3>This is *markdown* content</Text>
 *     <Table citation-id=/page/0/Table/4><table>...</table></Table>
 *   </Page>
 * </Document>
 * ```
 */

/** Hook: parse the XML-like content into a DOM element */
function useParsedXml(content: string | null): ParentNode | null {
  return useMemo(() => {
    if (!content) return null;
    // Ensure ampersands not part of an entity are encoded, to prevent parsing errors.
    // This regex looks for ampersands that are NOT followed by a known entity pattern (e.g., &amp;, &lt;, &#123;).
    const cleaned = content.replace(/&(?!([a-zA-Z0-9#]+);)/g, '&amp;');
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, 'application/xml');
    // Check for parser errors and log them if any
    const parserError = doc.getElementsByTagName('parsererror');
    if (parserError.length > 0) {
      console.error('XML parsing error:', parserError[0].textContent);
      return null; // Or handle error appropriately
    }
    return doc.documentElement;
  }, [content]);
}

/** Wraps any node to add hover-outline + title tooltip per original docs */
const HoverWrapper: React.FC<{
  tag: string;
  citation?: string;
  children: React.ReactNode;
  citationPreviewMap?: Record<string, string>;
}> = ({ tag, citation, children, citationPreviewMap }) => {
  const hoverInfo = citation ? `${tag} (${citation})` : tag;
  const baseClasses =
    'relative hover:bg-blue-50/10 dark:hover:bg-blue-950/10 ' +
    'hover:outline hover:outline-1 hover:outline-blue-200/30 dark:hover:outline-blue-800/30 ' +
    'transition-colors';

  const previewUrl =
    citation && citationPreviewMap ? citationPreviewMap[citation] : undefined;

  if (previewUrl) {
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <div
            data-tag={tag}
            data-citation={citation}
            title={hoverInfo}
            className={baseClasses}
          >
            {children}
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-80">
          <img src={previewUrl} alt={`Preview for ${citation}`} />
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <div
      data-tag={tag}
      data-citation={citation}
      title={hoverInfo}
      className={baseClasses}
    >
      {children}
    </div>
  );
};

/** Renders a `<Table>` node by serializing inner HTML (raw HTML case) */
const TableNode: React.FC<{
  el: Element;
  citationPreviewMap?: Record<string, string>;
}> = ({ el, citationPreviewMap }) => {
  const citation = el.getAttribute('citation-id') ?? undefined;
  const innerHtml = Array.from(el.childNodes)
    .map((c) => new XMLSerializer().serializeToString(c))
    .join('');
  return (
    <HoverWrapper
      tag="Table"
      citation={citation}
      citationPreviewMap={citationPreviewMap}
    >
      <div dangerouslySetInnerHTML={{ __html: innerHtml }} />
    </HoverWrapper>
  );
};

/** Renders any leaf node's content as markdown */
const MarkdownNode: React.FC<{
  el: Element;
  citationPreviewMap?: Record<string, string>;
}> = ({ el, citationPreviewMap }) => {
  const citation = el.getAttribute('citation-id') ?? undefined;
  const raw = Array.from(el.childNodes)
    .map((c) =>
      c.nodeType === Node.TEXT_NODE
        ? c.textContent
        : new XMLSerializer().serializeToString(c),
    )
    .join('');
  return (
    <HoverWrapper
      tag={el.tagName}
      citation={citation}
      citationPreviewMap={citationPreviewMap}
    >
      <ReactMarkdown rehypePlugins={[rehypeRaw]}>{raw}</ReactMarkdown>
    </HoverWrapper>
  );
};

/** Recursively renders container nodes (Document, Page, Section, etc.) */
const ContainerNode: React.FC<{
  el: Element;
  citationPreviewMap?: Record<string, string>;
}> = ({ el, citationPreviewMap }) => {
  const citation = el.getAttribute('citation-id') ?? undefined;
  return (
    <HoverWrapper
      tag={el.tagName}
      citation={citation}
      citationPreviewMap={citationPreviewMap}
    >
      {Array.from(el.childNodes).map((child, idx) => {
        const key =
          child.nodeType === Node.ELEMENT_NODE
            ? (child as Element).getAttribute('citation-id') || idx
            : idx;
        return (
          <React.Fragment key={key}>
            {renderNode(child, citationPreviewMap)}
          </React.Fragment>
        );
      })}
    </HoverWrapper>
  );
};

/** Dispatcher: picks TableNode, MarkdownNode, or ContainerNode per original logic */
function renderNode(
  node: ChildNode,
  citationPreviewMap?: Record<string, string>,
): React.ReactNode {
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as Element;

  if (el.tagName === 'Table') {
    return (
      <TableNode
        el={el}
        key={el.getAttribute('citation-id') ?? 'table'}
        citationPreviewMap={citationPreviewMap}
      />
    );
  }

  const hasElementChildren = Array.from(el.childNodes).some(
    (c) => c.nodeType === Node.ELEMENT_NODE,
  );

  return hasElementChildren ? (
    <ContainerNode
      el={el}
      key={el.getAttribute('citation-id') ?? el.tagName}
      citationPreviewMap={citationPreviewMap}
    />
  ) : (
    <MarkdownNode
      el={el}
      key={el.getAttribute('citation-id') ?? el.tagName}
      citationPreviewMap={citationPreviewMap}
    />
  );
}

/** Top-level OCRPreview component wiring it all together */
export const OCRPreview: React.FC<Props> = ({ content, blocks }) => {
  const root = useParsedXml(content);

  const citationPreviewMap = useMemo(() => {
    return (
      blocks?.reduce<Record<string, string>>((acc, b) => {
        acc[b.block_id] = b.preview;
        return acc;
      }, {}) ?? {}
    );
  }, [blocks]);

  if (!root) {
    return <div>No markdown content available.</div>;
  }

  return (
    <div className="prose prose-xs">
      {renderNode(root as unknown as ChildNode, citationPreviewMap)}
    </div>
  );
};
